import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useSiteConfig,
  useGetCurrentApp,
  Applications,
  RegisteredApplications,
  selectApplicationName,
} from '@innovyze/stylovyze';
import {
  ADP_EVENTS,
  initBernoulliAdp,
  setAdpContext,
  trackAdpAppView,
  trackAdpWaypoint,
} from '@/services/analytics/adp';
import { BERNOULLI_ADP_KEY } from '@/config/adp.site-config';
import { fetchUserContext } from '@/services/userContextApi';

import { ChatInterface } from '@/components/chat/';
import { TermsAndConditions } from '@/components/chat/TermsAndConditions';
import { fetchTncStatus } from '@/utils/tncUtils';
import { BERNOULLI_EMBED_CONTEXT_APPLIED_EVENT } from '@/services/embedHostContext';
import { isEmbedHostContextReady } from '@/services/agentBaseUrl';

const EMBED_CONTEXT_TIMEOUT_MS = 15_000;

type WebContainerLike = {
  getSessionId?: () => string;
};

const getHostSessionId = () => {
  const hostBridge = (window as Window & { WebContainer?: WebContainerLike })
    .WebContainer;
  return typeof hostBridge?.getSessionId === 'function'
    ? hostBridge.getSessionId()
    : undefined;
};

export type BernoulliHomeProps = {
  /** When true, skip T&C gate (host app owns compliance) and avoid agent API calls that may hang. */
  embeddedInHost?: boolean;
};

const APP_TO_LINE_NAME: Partial<Record<Applications, string>> = {
  [Applications.Asset]: 'WLC360A',
  [Applications.Insight]: 'WLC360I',
};

const BernoulliHome = ({ embeddedInHost = false }: BernoulliHomeProps) => {
  const isIframeEmbed = embeddedInHost && window.self !== window.top;
  const { config } = useSiteConfig();
  const currentApp = useGetCurrentApp();
  const hostAppName = useSelector(selectApplicationName);
  /*
   * Asset is now generally available, so the Assistant is no longer behind the
   * tech preview terms there. Insight and the standalone app are still previews
   * and keep the gate; embedded hosts own compliance themselves.
   *
   * This keys off the host shell's registered application rather than
   * useGetCurrentApp(), which resolves from entitlements and silently falls
   * back to Insight for an Asset user with a single entitlement (or before
   * entitlements have loaded) — that would leave them gated.
   */
  const requiresTnc =
    !embeddedInHost && hostAppName !== RegisteredApplications.APP_AM_MAIN;
  const [tncUpToDate, setTncUpToDate] = useState<boolean | null>(
    requiresTnc ? null : true,
  );
  const [embedReady, setEmbedReady] = useState(!isIframeEmbed);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [previouslyAcceptedVersion, setPreviouslyAcceptedVersion] = useState<
    string | null
  >(null);
  const foregroundStartRef = useRef<number>(0);
  const activeDurationMsRef = useRef<number>(0);

  useEffect(() => {
    if (!requiresTnc) {
      return;
    }
    const checkTncStatus = async () => {
      const status = await fetchTncStatus();
      setTncUpToDate(status.tnc_up_to_date);
      setPreviouslyAcceptedVersion(status.previously_accepted_version);
    };
    checkTncStatus();
  }, [requiresTnc]);

  useEffect(() => {
    if (!isIframeEmbed) {
      return;
    }

    const syncEmbedReady = () => {
      if (isEmbedHostContextReady()) {
        setEmbedReady(true);
        setEmbedError(null);
      }
    };

    syncEmbedReady();

    const timeoutId = window.setTimeout(() => {
      if (!isEmbedHostContextReady()) {
        setEmbedError(
          'Autodesk Assistant could not receive host authentication context.',
        );
      }
    }, EMBED_CONTEXT_TIMEOUT_MS);

    window.addEventListener(
      BERNOULLI_EMBED_CONTEXT_APPLIED_EVENT,
      syncEmbedReady,
    );

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener(
        BERNOULLI_EMBED_CONTEXT_APPLIED_EVENT,
        syncEmbedReady,
      );
    };
  }, [isIframeEmbed]);

  useEffect(() => {
    if (embeddedInHost) {
      return;
    }
    const lineName =
      (config?.adp as Record<string, string> | undefined)?.line_name ??
      APP_TO_LINE_NAME[currentApp];
    if (lineName) {
      sessionStorage.setItem('productLineName', lineName);
    }
  }, [config, currentApp, embeddedInHost]);

  const adp = useMemo(() => {
    const adpConfig = config?.adp;
    const hostReferrer =
      adpConfig?.id && adpConfig.name && adpConfig.key !== BERNOULLI_ADP_KEY
        ? {
            id: adpConfig.id,
            name: adpConfig.name,
            sessionId: getHostSessionId(),
          }
        : undefined;
    return initBernoulliAdp({
      runtimeConfig: adpConfig,
      hostReferrer,
    });
  }, [config]);

  useEffect(() => {
    // Enables host product lineage when running as embedded add-in.
    adp?.enableHostProductData();

    // Fetch ADP context before firing the app entry event so all
    // attributes (hub_id, project_id, team_id, account_tier) are present.
    fetchUserContext()
      .then(ctx => {
        if (ctx.adp_context) {
          setAdpContext(ctx.adp_context);
        }
      })
      .catch(err => {
        console.warn('Failed to fetch ADP context:', err);
      })
      .finally(() => {
        trackAdpAppView(adp);
      });
  }, [adp]);

  useEffect(() => {
    const now = Date.now();
    foregroundStartRef.current =
      document.visibilityState === 'visible' ? now : 0;

    const flushForegroundDuration = () => {
      if (foregroundStartRef.current > 0) {
        activeDurationMsRef.current += Date.now() - foregroundStartRef.current;
        foregroundStartRef.current = 0;
      }

      trackAdpWaypoint(adp, ADP_EVENTS.APP_FOREGROUND_DURATION, {
        'COMMON.DURATION_S': String(
          Math.round(activeDurationMsRef.current / 1000),
        ),
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        foregroundStartRef.current = Date.now();
        return;
      }

      if (foregroundStartRef.current > 0) {
        activeDurationMsRef.current += Date.now() - foregroundStartRef.current;
        foregroundStartRef.current = 0;
      }
    };

    const onPageHide = () => {
      flushForegroundDuration();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      flushForegroundDuration();
    };
  }, [adp]);

  const shellClass = embeddedInHost
    ? 'flex flex-1 min-h-0 w-full flex-col'
    : 'h-full w-full';

  if (requiresTnc && tncUpToDate === null) {
    return (
      <div className={`${shellClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted-foreground border-t-transparent" />
      </div>
    );
  }

  if (isIframeEmbed && embedError) {
    return (
      <div
        className={`${shellClass} flex items-center justify-center px-6 text-center`}>
        <p className="text-sm text-muted-foreground">{embedError}</p>
      </div>
    );
  }

  if (isIframeEmbed && !embedReady) {
    return (
      <div className={`${shellClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted-foreground border-t-transparent" />
      </div>
    );
  }

  if (requiresTnc && !tncUpToDate) {
    return (
      <div className={shellClass}>
        <TermsAndConditions
          onAccepted={() => setTncUpToDate(true)}
          isUpdated={previouslyAcceptedVersion !== null}
        />
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <ChatInterface />
    </div>
  );
};

export default BernoulliHome;
